import { Action, ActionReducer, createFeature, createReducer, on } from '@ngrx/store';

import { CompetitorSubMarket } from '../../models/competitor-chart-data.model';
import { PurchaseProduct } from '../../models/purchase-product.model';
import * as ProductOverviewActions from './product-overview.actions';
import {
  initialState,
  ProductOverviewState,
  PurchaseProductsState,
} from './product-overview.state';

// The same candidate can appear in both sub-markets, so both are patched.
function withNotesCount(
  state: PurchaseProductsState,
  productId: string,
  notesCount: number
): PurchaseProductsState {
  return {
    ...state,
    data:
      state.data?.map(
        (product: PurchaseProduct): PurchaseProduct =>
          product.candidate.productId === productId
            ? { ...product, candidate: { ...product.candidate, notesCount } }
            : product
      ) ?? null,
  };
}

export const productOverviewReducer: ActionReducer<
  ProductOverviewState,
  Action<string>
> = createReducer(
  initialState,

  on(
    ProductOverviewActions.loadCompetitors,
    (state, { productId }): ProductOverviewState => ({
      ...state,
      productId,
      competitors: { ...state.competitors, loading: true, error: null },
    })
  ),

  on(
    ProductOverviewActions.loadCompetitorsSuccess,
    (state, { competitors }): ProductOverviewState => ({
      ...state,
      competitors: { ...state.competitors, competitors, loading: false },
    })
  ),

  on(
    ProductOverviewActions.loadCompetitorsFailure,
    (state, { error }): ProductOverviewState => ({
      ...state,
      competitors: { ...state.competitors, loading: false, error },
    })
  ),

  on(
    ProductOverviewActions.loadPriceDevelopment,
    (state, { productId }): ProductOverviewState => ({
      ...state,
      productId,
      priceDevelopment: { ...state.priceDevelopment, loading: true, error: null },
    })
  ),

  on(
    ProductOverviewActions.setChartPeriod,
    (state, { periodMonths }): ProductOverviewState => ({
      ...state,
      chartPeriodMonths: periodMonths,
      priceDevelopment: { ...state.priceDevelopment, loading: true, error: null },
      salesDevelopment: {
        [CompetitorSubMarket.Primary]: {
          ...state.salesDevelopment[CompetitorSubMarket.Primary],
          loading: true,
          error: null,
        },
        [CompetitorSubMarket.Hospital]: {
          ...state.salesDevelopment[CompetitorSubMarket.Hospital],
          loading: true,
          error: null,
        },
      },
    })
  ),

  on(
    ProductOverviewActions.loadPriceDevelopmentSuccess,
    (state, { data }): ProductOverviewState => ({
      ...state,
      priceDevelopment: { ...state.priceDevelopment, data, loading: false },
    })
  ),

  on(
    ProductOverviewActions.loadPriceDevelopmentFailure,
    (state, { error }): ProductOverviewState => ({
      ...state,
      priceDevelopment: { ...state.priceDevelopment, loading: false, error },
    })
  ),

  on(
    ProductOverviewActions.loadSalesDevelopment,
    (state, { productId, subMarket }): ProductOverviewState => ({
      ...state,
      productId,
      salesDevelopment: {
        ...state.salesDevelopment,
        [subMarket]: { ...state.salesDevelopment[subMarket], loading: true, error: null },
      },
    })
  ),

  on(
    ProductOverviewActions.loadSalesDevelopmentSuccess,
    (state, { subMarket, data }): ProductOverviewState => ({
      ...state,
      salesDevelopment: {
        ...state.salesDevelopment,
        [subMarket]: { ...state.salesDevelopment[subMarket], data, loading: false },
      },
    })
  ),

  on(
    ProductOverviewActions.loadSalesDevelopmentFailure,
    (state, { subMarket, error }): ProductOverviewState => ({
      ...state,
      salesDevelopment: {
        ...state.salesDevelopment,
        [subMarket]: { ...state.salesDevelopment[subMarket], loading: false, error },
      },
    })
  ),

  on(
    ProductOverviewActions.loadIpgSummary,
    (state, { productId }): ProductOverviewState => ({
      ...state,
      productId,
      ipgSummary: { ...state.ipgSummary, loading: true, error: null },
    })
  ),

  on(
    ProductOverviewActions.loadIpgSummarySuccess,
    (state, { results }): ProductOverviewState => ({
      ...state,
      ipgSummary: { ...state.ipgSummary, data: results, loading: false },
    })
  ),

  // Empty results instead of null, so the summary shows "No data available." rather than a skeleton.
  on(
    ProductOverviewActions.loadIpgSummaryFailure,
    (state, { error }): ProductOverviewState => ({
      ...state,
      ipgSummary: { ...state.ipgSummary, data: [], loading: false, error },
    })
  ),

  on(
    ProductOverviewActions.loadPurchaseProducts,
    (state, { productId, subMarket }): ProductOverviewState => ({
      ...state,
      productId,
      purchaseProducts: {
        ...state.purchaseProducts,
        [subMarket]: { ...state.purchaseProducts[subMarket], loading: true, error: null },
      },
    })
  ),

  on(
    ProductOverviewActions.loadPurchaseProductsSuccess,
    (state, { subMarket, products }): ProductOverviewState => ({
      ...state,
      purchaseProducts: {
        ...state.purchaseProducts,
        [subMarket]: { ...state.purchaseProducts[subMarket], data: products, loading: false },
      },
    })
  ),

  on(
    ProductOverviewActions.loadPurchaseProductsFailure,
    (state, { subMarket, error }): ProductOverviewState => ({
      ...state,
      purchaseProducts: {
        ...state.purchaseProducts,
        [subMarket]: { ...state.purchaseProducts[subMarket], data: null, loading: false, error },
      },
    })
  ),

  on(
    ProductOverviewActions.setPurchaseProductNotesCount,
    (state, { productId, notesCount }): ProductOverviewState => ({
      ...state,
      purchaseProducts: {
        [CompetitorSubMarket.Primary]: withNotesCount(
          state.purchaseProducts[CompetitorSubMarket.Primary],
          productId,
          notesCount
        ),
        [CompetitorSubMarket.Hospital]: withNotesCount(
          state.purchaseProducts[CompetitorSubMarket.Hospital],
          productId,
          notesCount
        ),
      },
    })
  ),

  on(
    ProductOverviewActions.loadProduct,
    (state): ProductOverviewState => ({
      ...state,
      product: { ...state.product, loading: true, error: null },
    })
  ),

  on(
    ProductOverviewActions.loadProductSuccess,
    (state, { product }): ProductOverviewState => ({
      ...state,
      product: { ...state.product, data: product, loading: false },
    })
  ),

  on(
    ProductOverviewActions.loadProductFailure,
    (state, { error }): ProductOverviewState => ({
      ...state,
      product: { ...state.product, data: null, loading: false, error },
    })
  ),

  on(
    ProductOverviewActions.loadAnalysisSettings,
    (state): ProductOverviewState => ({
      ...state,
      analysisSettings: { ...state.analysisSettings, loading: true, error: null },
    })
  ),

  on(
    ProductOverviewActions.loadAnalysisSettingsSuccess,
    (state, { settings }): ProductOverviewState => ({
      ...state,
      analysisSettings: { ...state.analysisSettings, data: settings, loading: false },
    })
  ),

  on(
    ProductOverviewActions.loadAnalysisSettingsFailure,
    (state, { error }): ProductOverviewState => ({
      ...state,
      analysisSettings: { ...state.analysisSettings, data: null, loading: false, error },
    })
  )
);

export const productOverviewFeature: ReturnType<
  typeof createFeature<string, ProductOverviewState>
> = createFeature({
  name: 'productOverview',
  reducer: productOverviewReducer,
});
